import { Button, Modal, OverflowTooltip } from "@4uhub/lib4uhub";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import CustomLoading from "../../../../../../components/CustomLoading";
import useCopy from "../../../../../../hooks/useCopy";
import { IESignAutomationVariable } from "../../../../../../models/esign-rule";

const translationPath = "4uESign.page.automations.automationRules.";

interface IProps {
  open: boolean;
  loading: boolean;
  error?: string | null;
  variables: IESignAutomationVariable[];
  onClose: () => void;
  onRetry: () => void;
}

const getFirstString = (...values: unknown[]) =>
  values.find((value) => typeof value === "string" && value.trim()) as
    | string
    | undefined;

const formatVariableForClipboard = (value: string) => {
  const trimmed = value.trim();

  if (trimmed.startsWith("{{") && trimmed.endsWith("}}")) {
    return trimmed;
  }

  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    return `{{${trimmed.slice(1, -1)}}}`;
  }

  return `{{${trimmed}}}`;
};

export const AvailableVariablesModal = memo(
  ({ open, loading, error, variables, onClose, onRetry }: IProps) => {
    const { t } = useTranslation();
    const { copy } = useCopy();

    const items = useMemo(
      () =>
        variables.map((variable) => {
          const title =
            getFirstString(variable.name, variable.description, variable.key) ||
            "-";
          const code = getFirstString(variable.code, variable.key);
          const description = getFirstString(variable.description);
          const placeholder = getFirstString(variable.placeholder);
          const copyValue =
            getFirstString(
              variable.placeholder,
              variable.code,
              variable.name,
            );

          return {
            id: variable.id || `${title}-${code || "variable"}`,
            title,
            code,
            description,
            placeholder,
            copyValue: copyValue ? formatVariableForClipboard(copyValue) : "",
          };
        }),
      [variables],
    );

    return (
      <Modal
        open={open}
        onClose={onClose}
        title={
          t(translationPath + "available_variables") || "Variáveis disponíveis"
        }
      >
        <Box pt={2} minWidth={{ xs: "100%", sm: 520 }}>
          {loading ? (
            <CustomLoading />
          ) : error ? (
            <Stack spacing={2}>
              <Alert severity="error">{error}</Alert>
              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={onClose}>
                  {t(translationPath + "close") || "Fechar"}
                </Button>
                <Button variant="contained" onClick={onRetry}>
                  {t(translationPath + "try_again") || "Tentar novamente"}
                </Button>
              </Stack>
            </Stack>
          ) : items.length === 0 ? (
            <Stack spacing={2}>
              <Alert severity="info">
                {t(translationPath + "no_available_variables") ||
                  "Nenhuma variável disponível para esta automação."}
              </Alert>
              <Stack direction="row" justifyContent="flex-end">
                <Button variant="outlined" onClick={onClose}>
                  {t(translationPath + "close") || "Fechar"}
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <List disablePadding sx={{ maxHeight: 420, overflowY: "auto" }}>
                {items.map((item) => (
                  <ListItem
                    key={item.id}
                    disablePadding
                    sx={{
                      mb: 1.5,
                      px: 2,
                      py: 1.5,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      alignItems: "flex-start",
                    }}
                    secondaryAction={
                      item.copyValue ? (
                        <Tooltip
                          title={
                            t(translationPath + "copy_variable") ||
                            "Copiar variável"
                          }
                        >
                          <IconButton
                            edge="end"
                            onClick={() => copy(item.copyValue)}
                            size="small"
                          >
                            <ContentCopyRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : undefined
                    }
                  >
                    <ListItemText
                      primary={
                        <Stack spacing={1}>
                          <OverflowTooltip
                            text={item.title}
                            tooltip={item.title}
                            textTransform="none"
                            variant="body2"
                          />
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {item.code ? (
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${t(translationPath + "code") || "Código"}: ${item.code}`}
                              />
                            ) : null}
                            {item.placeholder &&
                            item.placeholder !== item.code &&
                            item.placeholder !== item.title ? (
                              <Chip
                                size="small"
                                color="primary"
                                variant="outlined"
                                label={item.placeholder}
                              />
                            ) : null}
                          </Stack>
                        </Stack>
                      }
                      secondary={
                        item.description &&
                        item.description !== item.title &&
                        item.description !== item.code ? (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            {item.description}
                          </Typography>
                        ) : null
                      }
                      sx={{ m: 0, pr: 6 }}
                    />
                  </ListItem>
                ))}
              </List>

              <Stack direction="row" justifyContent="flex-end">
                <Button variant="outlined" onClick={onClose}>
                  {t(translationPath + "close") || "Fechar"}
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>
      </Modal>
    );
  },
);
